/**
 * Shared breakpoint utilities that read values directly from Tailwind v4
 * CSS custom properties (e.g. `--breakpoint-lg: 64rem`), keeping JS in
 * sync with the Tailwind theme as the single source of truth.
 */

/** Fallback value for `--breakpoint-lg` when running outside a browser (SSR) */
const LG_FALLBACK_PX = 1024;

/**
 * Parses a CSS length value (e.g. `"64rem"`, `"1024px"`) into pixels.
 * Only supports `rem` and `px` units. Returns `null` for anything else.
 */
function cssToPx(value: string): number | null {
  const trimmed = value.trim();

  if (trimmed.endsWith("rem")) {
    const rem = parseFloat(trimmed);
    if (Number.isNaN(rem)) return null;
    const rootFontSize = parseFloat(
      getComputedStyle(document.documentElement).fontSize,
    );
    return rem * rootFontSize;
  }

  if (trimmed.endsWith("px")) {
    const px = parseFloat(trimmed);
    return Number.isNaN(px) ? null : px;
  }

  return null;
}

/**
 * Reads a Tailwind `--breakpoint-*` CSS custom property from the document
 * root and returns its value in pixels.
 */
function readBreakpoint(name: string, fallback: number): number {
  if (typeof window === "undefined") return fallback;

  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();

  if (!raw) return fallback;

  return cssToPx(raw) ?? fallback;
}

/**
 * Returns the Tailwind `lg` breakpoint in pixels, read from the
 * `--breakpoint-lg` CSS custom property. Falls back to 1024px during SSR
 * or if the property is unavailable.
 */
export function getLgBreakpoint(): number {
  return readBreakpoint("--breakpoint-lg", LG_FALLBACK_PX);
}
